import { useChecklistStore } from '../../../../stores/checklistStore';
import { useTranslation } from 'react-i18next';
import { useGoServerStore } from '../../../../stores/goServerStore';
import CategoryList from '../ChecklistSettings/CategoryList';
import DeleteConfirmationModal from '../ChecklistSettings/DeleteConfirmationModal';
import { Button, Spinner, Modal, Textarea, Toast, ToastToggle, Tooltip } from 'flowbite-react';
import { PlusIcon, ClipboardDocumentIcon, CheckIcon, XMarkIcon, SparklesIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import React, { useState } from 'react';

interface ParsedChecklistCategory {
    name: string;
    questions: string[];
}

interface ParsedChecklistImport {
    categories: ParsedChecklistCategory[];
}

const HEADING_PATTERNS = [
    /^(?:שלב|קטגוריה|חלק)\s*\d*\s*[:\-–].+$/i,
    /^(?:stage|category|section|part)\s*\d*\s*[:\-–].+$/i,
];

const isChecklistHeading = (line: string): boolean => {
    const normalized = line.trim();
    if (!normalized) {
        return false;
    }

    return HEADING_PATTERNS.some((pattern) => pattern.test(normalized));
};

const parsePastedChecklistText = (text: string): ParsedChecklistImport | null => {
    const lines = text
        .replace(/\r\n?/g, '\n')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

    if (lines.length === 0) {
        return null;
    }

    const categories: ParsedChecklistCategory[] = [];
    let currentCategory: ParsedChecklistCategory | null = null;

    for (const line of lines) {
        if (isChecklistHeading(line)) {
            currentCategory = { name: line, questions: [] };
            categories.push(currentCategory);
            continue;
        }

        if (!currentCategory) {
            return null;
        }

        currentCategory.questions.push(line);
    }

    if (categories.length === 0 || categories.some((category) => category.questions.length === 0)) {
        return null;
    }

    return { categories };
};

interface ChecklistLayoutProps {
    centerId: string;
    variant?: 'default' | 'onboarding';
}

const ChecklistLayout: React.FC<ChecklistLayoutProps> = ({ centerId, variant = 'default' }) => {
    const {
        loading,
        categories,
        questions,
        createCategory,
        createQuestion,
        reorderCategories,
        fetchStructure,
        centerDescription,
        deleteAllChecklistData
    } = useChecklistStore();

    const { t, i18n } = useTranslation();
    const { sdk } = useGoServerStore();
    const [isPasteModalOpen, setIsPasteModalOpen] = useState(false);
    const [pasteContent, setPasteContent] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'success' | 'error'>('success');
    const [isDeleteAllModalOpen, setIsDeleteAllModalOpen] = useState(false);
    const [isDeletingAll, setIsDeletingAll] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );


    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = categories.findIndex((c) => c.id === active.id);
            const newIndex = categories.findIndex((c) => c.id === over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                const newCategories = arrayMove(categories, oldIndex, newIndex);
                reorderCategories(newCategories);
            }
        }
    };

    const [isAddingBottom, setIsAddingBottom] = useState(false);
    const [bottomCategoryName, setBottomCategoryName] = useState('');
    const hasChecklistData = categories.length > 0 || questions.length > 0;

    const handleAddBottomCategory = async () => {
        if (!bottomCategoryName.trim() || isDeletingAll) return;
        await createCategory(centerId, bottomCategoryName);
        setBottomCategoryName('');
        setIsAddingBottom(false);
    };

    const handleDeleteAllChecklist = async () => {
        if (!centerId || isDeletingAll) return;

        setIsDeletingAll(true);
        try {
            await deleteAllChecklistData(centerId);
            setToastMessage(t('checklistCustomization.messages.clearAllSuccess', {
                defaultValue: 'All checklist categories and questions were deleted',
            }));
            setToastType('success');
            setShowToast(true);
            setIsDeleteAllModalOpen(false);
        } catch (error) {
            console.error('[ChecklistLayout] Failed to delete all checklist data:', error);
            setToastMessage(t('checklistCustomization.messages.clearAllError', {
                defaultValue: 'Failed to delete all checklist categories and questions',
            }));
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsDeletingAll(false);
        }
    };

    const importChecklistStructure = async (parsedChecklist: ParsedChecklistImport) => {
        let count = 0;

        for (const category of parsedChecklist.categories) {
            const newCategory = await createCategory(centerId, category.name);

            // Optimistic create returns the saved category object in normal flow.
            if (!newCategory || !('id' in newCategory) || !newCategory.id) {
                throw new Error(`Failed to create category: ${category.name}`);
            }

            for (const questionText of category.questions) {
                await createQuestion(centerId, questionText, newCategory.id);
                count++;
            }
        }

        if (count === 0) {
            throw new Error('No questions found in parsed checklist');
        }

        setToastMessage(t('checklistCustomization.messages.generatedSuccess', {
            count,
            defaultValue: `Generated categories and ${count} questions`,
        }));
        setToastType('success');
        setShowToast(true);
        setIsPasteModalOpen(false);
        setPasteContent('');
        fetchStructure(centerId);
    };

    const generateQuestionsFromText = (text: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            setIsGenerating(true);

            try {
                const parsedChecklist = parsePastedChecklistText(text);
                if (parsedChecklist) {
                    importChecklistStructure(parsedChecklist)
                        .then(resolve)
                        .catch((error) => {
                            console.error('Error importing parsed checklist:', error);
                            setToastMessage(t('checklistCustomization.messages.generateError', { defaultValue: 'Failed to generate checklist' }));
                            setToastType('error');
                            setShowToast(true);
                            reject(error);
                        })
                        .finally(() => {
                            setIsGenerating(false);
                        });
                    return;
                }

                if (!sdk) {
                    throw new Error('Audio server SDK not initialized');
                }

                const ws = sdk.sales.wsCallPlanToChecklist({
                    onOpen: () => {
                        console.log('WS Connected, sending plan text...');
                        ws.send(JSON.stringify({ planText: text }));
                    },
                    onMessage: async (data: any) => {
                        // Skip heartbeat pings and non-result messages
                        if (data?.type === 'ping' || data?.type === 'pong') return;

                        try {
                            let questionsData: any = null;

                            console.log('[ChecklistLayout] Raw WS data:', typeof data, JSON.stringify(data));

                            // Check if server returned an error
                            if (data?.error) {
                                throw new Error(`Server error: ${data.error}`);
                            }

                            // data.result can be a JSON string, an already-parsed object, or empty
                            // Use || instead of ?? so empty string "" also falls through
                            const rawResult = data?.result || data;
                            if (typeof rawResult === 'string' && rawResult.trim()) {
                                try {
                                    questionsData = JSON.parse(rawResult);
                                } catch (jsonErr) {
                                    console.error('Failed to parse AI response as JSON:', jsonErr);
                                    questionsData = rawResult.split('\n').filter((l: string) => l.trim().length > 0);
                                }
                            } else if (typeof rawResult === 'object' && rawResult !== null) {
                                // Already parsed — check if it directly has categories
                                if (rawResult.categories) {
                                    questionsData = rawResult;
                                } else if (rawResult.result) {
                                    // Nested: {result: {categories: [...]}}
                                    questionsData = typeof rawResult.result === 'string' ? JSON.parse(rawResult.result) : rawResult.result;
                                } else {
                                    questionsData = rawResult;
                                }
                            }

                            console.log('Parsed data from AI:', questionsData);

                            if (questionsData && centerId) {
                                if (questionsData.categories && Array.isArray(questionsData.categories)) {
                                    let count = 0;
                                    for (const cat of questionsData.categories) {
                                        if (cat.name && cat.questions && Array.isArray(cat.questions)) {
                                            const newCat = await createCategory(centerId, cat.name);
                                            // @ts-ignore
                                            if (newCat && newCat.id) {
                                                for (const qText of cat.questions) {
                                                    if (typeof qText === 'string') {
                                                        // @ts-ignore
                                                        await createQuestion(centerId, qText, newCat.id);
                                                        count++;
                                                    }
                                                }
                                            }
                                        }
                                    }
                                    if (count > 0) {
                                        setToastMessage(t('checklistCustomization.messages.generatedSuccess', { count, defaultValue: `Generated categories and ${count} questions` }));
                                        setToastType('success');
                                        setShowToast(true);
                                        setIsPasteModalOpen(false);
                                        setPasteContent('');
                                        fetchStructure(centerId);
                                        resolve();
                                        return;
                                    }
                                }

                                let flatQuestions: string[] = [];
                                if (Array.isArray(questionsData)) {
                                    flatQuestions = questionsData;
                                } else if (questionsData.questions && Array.isArray(questionsData.questions)) {
                                    flatQuestions = questionsData.questions;
                                }

                                if (flatQuestions.length > 0) {
                                    let count = 0;
                                    for (const qText of flatQuestions) {
                                        if (typeof qText === 'string') {
                                            await createQuestion(centerId, qText);
                                            count++;
                                        } else if (typeof qText === 'object' && (qText as any).text) {
                                            await createQuestion(centerId, (qText as any).text);
                                            count++;
                                        }
                                    }
                                    setToastMessage(t('checklistCustomization.messages.generatedSuccess', { count, defaultValue: `Generated ${count} questions` }));
                                    setToastType('success');
                                    setShowToast(true);
                                    setIsPasteModalOpen(false);
                                    setPasteContent('');
                                    fetchStructure(centerId);
                                    resolve();
                                } else {
                                    if (!questionsData.categories) {
                                        throw new Error('No valid categories or questions found in AI response');
                                    }
                                }
                            } else {
                                throw new Error('No data found in AI response');
                            }
                        } catch (error) {
                            console.error('Error generating checklist:', error);
                            setToastMessage(t('checklistCustomization.messages.generateError', { defaultValue: 'Failed to generate checklist' }));
                            setToastType('error');
                            setShowToast(true);
                            reject(error);
                        } finally {
                            ws.close();
                            setIsGenerating(false);
                        }
                    },
                    onError: (error: any) => {
                        console.error('WebSocket error:', error);
                        setToastMessage(t('checklistCustomization.messages.connectionError', { defaultValue: 'Connection error. Is the audio server running?' }));
                        setToastType('error');
                        setShowToast(true);
                        setIsGenerating(false);
                        reject(error);
                    }
                });
            } catch (e) {
                console.error('WebSocket Init Error:', e);
                setToastMessage(t('checklistCustomization.messages.connectionError', { defaultValue: 'Failed to connect to audio server' }));
                setToastType('error');
                setShowToast(true);
                setIsGenerating(false);
                reject(e);
            }
        });
    };

    const generateQuestionsFromBusinessInfo = (): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (!centerDescription || centerDescription.trim() === '') {
                setToastMessage(t('checklistCustomization.tooltips.emptyBusinessInfo', { defaultValue: 'Please fill in the business info section above first' }));
                setToastType('error');
                setShowToast(true);
                reject(new Error('No business info'));
                return;
            }

            setIsGenerating(true);

            try {
                if (!sdk) {
                    throw new Error('Audio server SDK not initialized');
                }

                const ws = sdk.sales.wsBusinessInfoToChecklist({
                    onOpen: () => {
                        console.log('WS Connected, sending business info...');
                        ws.send(JSON.stringify({ businessInfo: centerDescription }));
                    },
                    onMessage: async (data: any) => {
                        // Skip heartbeat pings and non-result messages
                        if (data?.type === 'ping' || data?.type === 'pong') return;

                        try {
                            let questionsData: any = null;

                            console.log('[ChecklistLayout BusinessInfo] Raw WS data:', typeof data, JSON.stringify(data));

                            if (data?.error) {
                                throw new Error(`Server error: ${data.error}`);
                            }

                            const rawResult = data?.result || data;
                            if (typeof rawResult === 'string' && rawResult.trim()) {
                                try {
                                    questionsData = JSON.parse(rawResult);
                                } catch (jsonErr) {
                                    console.error('Failed to parse AI response as JSON:', jsonErr);
                                    questionsData = rawResult.split('\n').filter((l: string) => l.trim().length > 0);
                                }
                            } else if (typeof rawResult === 'object' && rawResult !== null) {
                                if (rawResult.categories) {
                                    questionsData = rawResult;
                                } else if (rawResult.result) {
                                    questionsData = typeof rawResult.result === 'string' ? JSON.parse(rawResult.result) : rawResult.result;
                                } else {
                                    questionsData = rawResult;
                                }
                            }

                            console.log('Parsed data from AI (BusinessInfo):', questionsData);

                            if (questionsData && centerId) {
                                if (questionsData.categories && Array.isArray(questionsData.categories)) {
                                    let count = 0;
                                    for (const cat of questionsData.categories) {
                                        if (cat.name && cat.questions && Array.isArray(cat.questions)) {
                                            const newCat = await createCategory(centerId, cat.name);
                                            // @ts-ignore
                                            if (newCat && newCat.id) {
                                                for (const qText of cat.questions) {
                                                    if (typeof qText === 'string') {
                                                        // @ts-ignore
                                                        await createQuestion(centerId, qText, newCat.id);
                                                        count++;
                                                    }
                                                }
                                            }
                                        }
                                    }
                                    if (count > 0) {
                                        setToastMessage(t('checklistCustomization.messages.generatedSuccess', { count, defaultValue: `Generated categories and ${count} questions` }));
                                        setToastType('success');
                                        setShowToast(true);
                                        fetchStructure(centerId);
                                        resolve();
                                        return;
                                    }
                                }

                                let flatQuestions: string[] = [];
                                if (Array.isArray(questionsData)) {
                                    flatQuestions = questionsData;
                                } else if (questionsData.questions && Array.isArray(questionsData.questions)) {
                                    flatQuestions = questionsData.questions;
                                }

                                if (flatQuestions.length > 0) {
                                    let count = 0;
                                    for (const qText of flatQuestions) {
                                        if (typeof qText === 'string') {
                                            await createQuestion(centerId, qText);
                                            count++;
                                        } else if (typeof qText === 'object' && (qText as any).text) {
                                            await createQuestion(centerId, (qText as any).text);
                                            count++;
                                        }
                                    }
                                    setToastMessage(t('checklistCustomization.messages.generatedSuccess', { count, defaultValue: `Generated ${count} questions` }));
                                    setToastType('success');
                                    setShowToast(true);
                                    fetchStructure(centerId);
                                    resolve();
                                } else {
                                    if (!questionsData.categories) {
                                        throw new Error('No valid categories or questions found in AI response');
                                    }
                                }
                            } else {
                                throw new Error('No data found in AI response');
                            }
                        } catch (error) {
                            console.error('Error generating checklist from business info:', error);
                            setToastMessage(t('checklistCustomization.messages.generateError', { defaultValue: 'Failed to generate checklist' }));
                            setToastType('error');
                            setShowToast(true);
                            reject(error);
                        } finally {
                            ws.close();
                            setIsGenerating(false);
                        }
                    },
                    onError: (error: any) => {
                        console.error('WebSocket error:', error);
                        setToastMessage(t('checklistCustomization.messages.connectionError', { defaultValue: 'Connection error. Is the audio server running?' }));
                        setToastType('error');
                        setShowToast(true);
                        setIsGenerating(false);
                        reject(error);
                    }
                });
            } catch (e) {
                console.error('WebSocket Init Error:', e);
                setToastMessage(t('checklistCustomization.messages.connectionError', { defaultValue: 'Failed to connect to audio server' }));
                setToastType('error');
                setShowToast(true);
                setIsGenerating(false);
                reject(e);
            }
        });
    };

    const actionButtons = (
        <>
            {variant !== 'onboarding' && (
                <Button
                    color="gray"
                    size="sm"
                    onClick={() => setIsPasteModalOpen(true)}
                    disabled={isGenerating || isDeletingAll}
                >
                    <ClipboardDocumentIcon className="mr-2 h-5 w-5" />
                    {t('checklistCustomization.actions.importScript', { defaultValue: 'Import Script' })}
                </Button>
            )}
            {variant !== 'onboarding' && (centerDescription && centerDescription.trim() ? (
                <Button
                    color="light"
                    size="sm"
                    disabled={isGenerating || isDeletingAll}
                    onClick={() => generateQuestionsFromBusinessInfo()}
                    className="flex items-center gap-1"
                >
                    {isGenerating ? <Spinner size="sm" className="mr-1" /> : <SparklesIcon className="mr-1 h-4 w-4" />}
                    {t('checklistCustomization.actions.generateFromInfo', { defaultValue: 'Generate from Info' })}
                </Button>
            ) : (
                <Tooltip content={t('checklistCustomization.tooltips.emptyBusinessInfo', { defaultValue: 'Please fill in the business info section above first' })}>
                    <Button
                        color="light"
                        size="sm"
                        disabled
                        className="flex cursor-not-allowed items-center gap-1 opacity-50"
                    >
                        <SparklesIcon className="mr-1 h-4 w-4" />
                        {t('checklistCustomization.actions.generateFromInfo', { defaultValue: 'Generate from Info' })}
                    </Button>
                </Tooltip>
            ))}
        </>
    );

    return (
        <div className={variant === 'onboarding' ? 'space-y-6' : 'space-y-6'}>
            <div className={`flex flex-wrap items-center ${variant === 'onboarding' ? 'justify-between gap-4' : 'gap-2'}`}>
                {variant === 'onboarding' ? (
                    <>
                        <button
                            type="button"
                            onClick={() => setIsDeleteAllModalOpen(true)}
                            disabled={!hasChecklistData || isGenerating || isDeletingAll}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-normal text-text-700 transition hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-40 dark:text-neutral-300 dark:hover:text-brand-300"
                        >
                            <TrashIcon className="h-4 w-4" />
                            <span>מחק הכל</span>
                        </button>
                        <div className="flex flex-wrap items-center gap-3">
                            {actionButtons}
                        </div>
                    </>
                ) : (
                    <>
                        {actionButtons}
                        <Button
                            color="failure"
                            size="sm"
                            onClick={() => setIsDeleteAllModalOpen(true)}
                            disabled={!hasChecklistData || isGenerating || isDeletingAll}
                            className="flex items-center gap-1"
                        >
                            {isDeletingAll ? <Spinner size="sm" className="mr-1" /> : <TrashIcon className="h-4 w-4 mr-1" />}
                            {t('checklistCustomization.actions.deleteAll', { defaultValue: 'Delete All' })}
                        </Button>
                    </>
                )}
            </div>

            {/* Categories List */}
            <div className="space-y-4">
                {loading ? (
                    <div className="flex justify-center p-8">
                        <Spinner size="xl" />
                    </div>
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <CategoryList
                            centerId={centerId}
                            variant={variant}
                        />
                    </DndContext>
                )}
            </div>

            {/* Bottom Add Category Button */}
            {!loading && (
                <div className="mt-4" dir={i18n.language === 'he' || i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                    {isAddingBottom ? (
                        <div className={`space-y-4 rounded-lg p-4 shadow-lg animate-in fade-in slide-in-from-top-2 ${variant === 'onboarding' ? 'border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800' : 'border border-brand-200 bg-white dark:border-brand-900 dark:bg-neutral-800'}`}>
                            <h4 className="font-semibold text-brand-600 dark:text-brand-300">
                                {t('checklistCustomization.actions.addCategory', { defaultValue: 'Add New Category' })}
                            </h4>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {t('checklistCustomization.placeholders.newCategoryName', { defaultValue: 'Category Name' })}
                                </label>
                                <input
                                    type="text"
                                    value={bottomCategoryName}
                                    onChange={(e) => setBottomCategoryName(e.target.value)}
                                    placeholder={t('checklistCustomization.placeholders.newCategoryName', { defaultValue: 'e.g. Greeting' })}
                                    className="block w-full rounded-lg border border-neutral-300 bg-neutral-50 p-2.5 text-sm text-neutral-900 focus:border-brand-500 focus:ring-brand-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white dark:placeholder:text-neutral-400"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleAddBottomCategory();
                                    }}
                                    disabled={isDeletingAll}
                                    autoFocus
                                />
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <Button size="sm" color="gray" onClick={() => setIsAddingBottom(false)} disabled={isDeletingAll}>
                                    {t('common.cancel', { defaultValue: 'Cancel' })}
                                </Button>
                                <Button
                                    size="sm"
                                    className="border-none bg-brand-500 text-white outline-none hover:bg-brand-600"
                                    onClick={handleAddBottomCategory}
                                    disabled={!bottomCategoryName.trim() || isDeletingAll}
                                >
                                    {t('common.add', { defaultValue: 'Add' })}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <Button
                            outline
                            className={variant === 'onboarding'
                                ? 'w-full !rounded-xl !border-2 !border-dashed !border-brand-200 !bg-transparent py-4 text-brand-700 hover:!bg-brand-50 focus:!ring-0 dark:!border-brand-800 dark:text-brand-300 dark:hover:!bg-brand-950/20'
                                : 'w-full border-dashed border-2 border-brand-500 text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/10'}
                            onClick={() => {
                                setIsAddingBottom(true);
                                setBottomCategoryName('');
                            }}
                            disabled={isDeletingAll}
                        >
                            <PlusIcon className="mr-2 h-5 w-5" />
                            {variant === 'onboarding' ? 'הוסף קטגוריית תסריט חדשה' : t('checklistCustomization.actions.addCategory', { defaultValue: 'Add Category' })}
                        </Button>
                    )}
                </div>
            )}

            {/* Paste Modal (Simplified) */}
            <Modal show={isPasteModalOpen} onClose={() => setIsPasteModalOpen(false)}>
                <div className="relative rounded-lg bg-white shadow dark:bg-neutral-800">
                    {/* Header */}
                    <div className="flex items-start justify-between rounded-t border-b border-neutral-200 p-4 dark:border-neutral-700">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                            {t('checklistCustomization.modals.pasteScriptTitle')}
                        </h3>
                        <button
                            type="button"
                            className="ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-neutral-700 dark:hover:text-white"
                            onClick={() => setIsPasteModalOpen(false)}
                        >
                            <svg aria-hidden="true" className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                            <span className="sr-only">Close modal</span>
                        </button>
                    </div>
                    {/* Body */}
                    <div className="max-h-[60vh] space-y-6 overflow-y-auto p-6">
                        <p className="text-base leading-relaxed text-gray-500 dark:text-gray-400">
                            {t('checklistCustomization.modals.pasteScriptDescription')}
                        </p>
                        <Textarea
                            rows={10}
                            value={pasteContent}
                            onChange={(e) => setPasteContent(e.target.value)}
                            placeholder={t('checklistCustomization.placeholders.pasteScript')}
                            theme={{ base: 'block w-full rounded-lg border border-neutral-300 bg-neutral-50 p-3 text-sm text-neutral-900 focus:border-brand-500 focus:ring-brand-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-neutral-100 dark:placeholder:text-neutral-400 dark:focus:border-brand-400 dark:focus:ring-brand-400' }}
                        />
                    </div>
                    {/* Footer */}
                    <div className="flex items-center justify-end space-x-2 rounded-b border-t border-neutral-200 p-6 dark:border-neutral-700">
                        <Button color="gray" onClick={() => setIsPasteModalOpen(false)} disabled={isGenerating || isDeletingAll}>{t('common.cancel')}</Button>
                        <Button
                            className="bg-brand-500 text-white hover:bg-brand-600"
                            disabled={!pasteContent.trim() || isGenerating || isDeletingAll}
                            onClick={() => generateQuestionsFromText(pasteContent)}
                        >
                            {isGenerating ? <Spinner size="sm" className="mr-2" /> : null}
                            {t('checklistCustomization.actions.generate')}
                        </Button>
                    </div>
                </div>
            </Modal>

            <DeleteConfirmationModal
                isOpen={isDeleteAllModalOpen}
                onClose={() => {
                    if (!isDeletingAll) {
                        setIsDeleteAllModalOpen(false);
                    }
                }}
                onConfirm={handleDeleteAllChecklist}
                title={t('checklistCustomization.modals.deleteAllTitle', {
                    defaultValue: 'Delete Entire Checklist',
                })}
                message={t('checklistCustomization.modals.deleteAllMessage', {
                    defaultValue: 'Are you sure you want to delete all checklist categories and questions? This action cannot be undone.',
                })}
            />

            {showToast && (
                <div className="fixed bottom-4 start-4 z-[100]">
                    <Toast>
                        <div className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${toastType === 'success' ? 'bg-green-100 text-green-500 dark:bg-green-800 dark:text-green-200' : 'bg-red-100 text-red-500 dark:bg-red-800 dark:text-red-200'
                            }`}>
                            {toastType === 'success' ? <CheckIcon className="h-5 w-5" /> : <XMarkIcon className="h-5 w-5" />}
                        </div>
                        <div className="ml-3 text-sm font-normal text-gray-800 dark:text-gray-200">
                            {toastMessage}
                        </div>
                        <ToastToggle onDismiss={() => setShowToast(false)} />
                    </Toast>
                </div>
            )}
        </div>
    );
};

export default ChecklistLayout;
